import { useAction, useAtom } from '@reatom/npm-react'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useMount } from 'react-use'
import styled from 'styled-components'
import { match, P } from 'ts-pattern'

import { ArrowRightIcon, Button } from '@blade/ui-kit'
import { Network, selectedNetworkAtom } from '@root/entities/network'
import {
  ConfirmTransferStep,
  InputTransferStep,
  reatomTransferFlow,
} from '@root/features/wallet/transfer'

import { TransferAmount } from './transfer-amount'
import { TransferCard } from './transfer-card'
import { TransferFeeEvm } from './transfer-fee/transfer-fee-evm'
import { TransferFeeSvm } from './transfer-fee/transfer-fee-svm'
import { TransferReviewModal } from './transfer-review/transfer-review-modal'

import type { WalletModel } from '@root/entities/wallet'

type TransferDetailsProps = {
  wallet: WalletModel
}

export const TransferDetails = (props: TransferDetailsProps) => {
  const { t } = useTranslation()

  const model = useMemo(
    () =>
      // eslint-disable-next-line @reatom/unit-naming-rule
      reatomTransferFlow({
        wallet: props.wallet,
      }),
    [props.wallet],
  )

  const [network] = useAtom(selectedNetworkAtom)
  const [validationError] = useAtom(model.validationAtom)
  const [step, setStep] = useAtom(model.stepAtom)

  const initialize = useAction(model.initializeAction)

  useMount(() => {
    initialize()
  })

  const renderFeeCard = () =>
    match(network)
      .with(Network.Solana, () => (
        <FeeCardSvm>
          <TransferFeeSvm model={model.svmFeeModel} />
        </FeeCardSvm>
      ))
      .with(P.union(Network.Ethereum, Network.Base, Network.Bsc), () => (
        <FeeCardEvm>
          <TransferFeeEvm model={model.gasSettings} />
        </FeeCardEvm>
      ))
      .with(Network.Ton, () => <></>)
      .exhaustive()

  return (
    <Root
      onSubmit={(e) => {
        e.preventDefault()
        setStep(new ConfirmTransferStep())
      }}
    >
      <TransferCard wallet={props.wallet} model={model} />
      <TransferAmount
        amountModel={model.amount}
        currencyModel={model.currency}
        validation={validationError}
      />
      {renderFeeCard()}
      <ActionContainer>
        <Button
          fullWidth
          color="primary"
          radius="rounded"
          type="submit"
          size="h36"
          disabled={!!validationError}
          iconLeft={<ArrowRightIcon size={26} />}
        >
          {t('common.send')}
        </Button>
      </ActionContainer>

      <TransferReviewModal
        open={step instanceof ConfirmTransferStep}
        onOpenChange={() => {
          setStep(new InputTransferStep())
        }}
        onDone={() => {
          setStep(new InputTransferStep())
        }}
        model={model}
      />
    </Root>
  )
}

const Root = styled.form`
  height: 100%;

  display: flex;
  flex-direction: column;
  padding-inline: 12px;
  padding-block-end: 10px;
  gap: 40px;
`

const ActionContainer = styled.div`
  margin-top: auto;
`
const FeeCard = styled.div`
  border-radius: 12px;
  border: 1px solid var(--background-3);
`
const FeeCardEvm = styled(FeeCard)`
  padding: 6px;
`
const FeeCardSvm = styled(FeeCard)`
  padding: 12px 6px;
`
